import { useState, useMemo, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import ColorPicker from './ColorPicker';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import API from '../../../API';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../utils/Auth';
import FramePreview from './FramePreview';
import styles from './WidgetGenerator.module.css';

const initialConfig = {
    qrId: '',
    name: '',
    tabColor: '#064056',
    tabText: '',
    title: '',
    drawerWidth: 250,
    drawerHeight: 300,
    displayOn: 'all',
    showLogo: true,
    animateTab: false,
    animateType: 'wiggle',
};

const FIXED_DRAWER_WIDTH = 250;
const FIXED_DRAWER_HEIGHT = 300;

const deepEqual = (obj1, obj2) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (let key of keys1) {
        if (obj1[key] !== obj2[key]) return false;
    }
    
    return true;
};

// main
export default function WidgetGenerator({qrId, generator}) {

    const { userObj, widgets, activeOrgId, liquidQRs, qr_options } = useAuth();
    const { getQR, createWidget, updateWidget } = API;

    const [ searchParams, ] = useSearchParams();

    // Derive the most up-to-date widget record from context if available
    const currentWidget = useMemo(() => {
        const id = generator?.id || null;
        if (!id || !widgets) return generator || null;
        return widgets.find(w => w.id === id) || generator || null;
    }, [widgets, generator]);

    // Initialize configuration prioritizing live widgets context, then generator, else defaults
    const getInitialConfig = () => {
        const source = currentWidget;
        if (source) {
            return {
                qrId: source.qr_id || '',
                name: source.name || '',
                tabColor: source.tab_color || '#064056',
                tabText: source.tab_text || '',
                title: source.title || '',
                drawerWidth: FIXED_DRAWER_WIDTH,
                drawerHeight: FIXED_DRAWER_HEIGHT,
                displayOn: source.display_on || 'all',
                showLogo: source.show_logo !== undefined ? source.show_logo : true,
                animateTab: source.qr_animate !== undefined ? source.qr_animate : false,
                animateType: source.qr_animate_type || 'wiggle',
            };
        }
        return { ...initialConfig, qrId: qrId || '' };
    };

    const [ lastGeneratedConfig, setLastGeneratedConfig ] = useState(getInitialConfig());
    const [ configuration, setConfiguration ] = useState(getInitialConfig());
    const [ scriptTag, setScriptTag ] = useState('')
    const [ widgetId, setWidgetId ] = useState(generator?.id || null);
    const [ widgetUpdatedAt, setWidgetUpdatedAt ] = useState(generator?.updated_at || null);
    const [ copied, setCopied ] = useState(false);
    const [ isGenerated, setIsGenerated ] = useState(false);
    const [ hasInitialGenerated, setHasInitialGenerated ] = useState(false);
    const [ isGenerating, setIsGenerating ] = useState(false);
    const [ stylingSaved, setStyleSaved ] = useState(false);
    const [ showColorPicker, setShowColorPicker ] = useState(false);
    const [ debouncedPreviewConfig, setDebouncedPreviewConfig ] = useState(getInitialConfig());
    const [ previewTrigger, setPreviewTrigger ] = useState(0);

    const selectedQr = useMemo(() => liquidQRs.find(qr => qr.id === configuration.qrId), [liquidQRs, configuration.qrId]);
    const connectDefaultUrl = useMemo(() => selectedQr?.data?.connect?.defaultUrl || '', [selectedQr]);
    const selectedQrStyle = useMemo(() => {
        // Prefer qr_options map if available, otherwise fall back to the loaded QR object
        if (qr_options && typeof qr_options === 'object') {
            const mappedStyle = qr_options[configuration.qrId]?.qr_style || qr_options.qr_style;
            if (mappedStyle) return mappedStyle;
        }
        return selectedQr?.data?.qr_style || null;
    }, [qr_options, selectedQr, configuration.qrId]);

    // Reset state when switching between generators/new widgets to avoid showing a stale QR
    useEffect(() => {
        const freshConfig = getInitialConfig();
        setConfiguration(freshConfig);
        setLastGeneratedConfig(freshConfig);
        setWidgetId(generator?.id || null);
        setWidgetUpdatedAt(generator?.updated_at || null);
        setScriptTag('');
        setIsGenerated(false);
        setHasInitialGenerated(false);
        setCopied(false);
    }, [generator, qrId]);

    // Keep updated_at in sync with the latest widget record from context
    useEffect(() => {
        if (!currentWidget) return;
        setWidgetUpdatedAt(currentWidget.updated_at || null);
    }, [currentWidget?.updated_at]);

    // On widgets context updates (e.g., after Save or page reload), resync configuration from DB
    useEffect(() => {
        if (!currentWidget) return;
        const fresh = getInitialConfig();
        // Only resync when not actively generating and when local config differs from DB
        const differs = !deepEqual(fresh, configuration);
        setWidgetUpdatedAt(currentWidget.updated_at || null);
        if (differs) {
            setConfiguration(fresh);
            setLastGeneratedConfig(fresh);
        }
    }, [currentWidget, widgets]);

    const createWidgetRecord = async () => {
        try {
            setIsGenerating(true);

            // 1. Fetch QR 
            const qrDataObj = liquidQRs.filter(qr => qr.id === configuration.qrId)[0];
            const qrData = qrDataObj.data;
            
            // 2. Build widget payload
            const widgetPayload = {
                qr_id: configuration.qrId,
                org_id: activeOrgId,
        
                // Widget name
                name: configuration.name,

                // Drawer configuration
                tab_color: configuration.tabColor,
                tab_text: configuration.tabText,
                title: configuration.title,
                drawer_width: FIXED_DRAWER_WIDTH,
                drawer_height: FIXED_DRAWER_HEIGHT,
                display_on: configuration.displayOn,
                show_logo: configuration.showLogo,
                qr_animate: configuration.animateTab,
                qr_animate_type: configuration.animateType,
        
                // QR styling configuration (flatten the nested objects)
                qr_width: qrData.qr_style.width,
                qr_height: qrData.qr_style.height,
                qr_shape:  qrData.qr_style.shape,
                qr_margin: qrData.qr_style.margin,
                qr_type: qrData.qr_style.type,
                qr_type_number: qrData.qr_style.qrOptions?.typeNumber,
                qr_mode: qrData.qr_style.qrOptions?.mode,
                qr_error_correction_level: qrData.qr_style.qrOptions?.errorCorrectionLevel,
        
                // Dots styling
                qr_dots_color: qrData.qr_style.dotsOptions?.color,
                qr_dots_type: qrData.qr_style.dotsOptions?.type,
                qr_dots_gradient: qrData.qr_style.dotsOptions?.gradient,
        
                // Corner squares
                qr_corners_square_color: qrData.qr_style.cornersSquareOptions?.color,
                qr_corners_square_type: qrData.qr_style.cornersSquareOptions?.type,
                qr_corners_square_gradient: qrData.qr_style.cornersSquareOptions?.gradient,
                
                // Corner dots
                qr_corners_dot_color: qrData.qr_style.cornersDotOptions?.color,
                qr_corners_dot_type: qrData.qr_style.cornersDotOptions?.type,
                qr_corners_dot_gradient: qrData.qr_style.cornersDotOptions?.gradient,
                
                // Background
                qr_background_color: qrData.qr_style.backgroundOptions?.color,
                qr_background_gradient: qrData.qr_style.backgroundOptions?.gradient,
        
                // Image/logo
                qr_image: qrData.qr_style.image,
                qr_image_hide_background_dots: qrData.qr_style.imageOptions?.hideBackgroundDots,
                qr_image_size: qrData.qr_style.imageOptions?.imageSize,
                qr_image_margin: qrData.qr_style.imageOptions?.margin,
                qr_image_cross_origin: qrData.qr_style.imageOptions?.crossOrigin
            };

            // 3. Create or update widget based on whether widgetId exists
            let resultWidgetId;
            let updatedAtTimestamp;
            if (widgetId) {
                // Update existing widget - trigger will auto-update updated_at
                const result = await updateWidget(widgetId, widgetPayload);
                if (!result.ok) {
                    throw new Error(result.error?.message || 'Widget update error');
                }
                resultWidgetId = widgetId;
                updatedAtTimestamp = result.data?.updated_at;
            } else {
                // Create new widget
                const widgetData = await createWidget(widgetPayload);
                if (!widgetData.success) {
                    throw new Error(widgetData.error || 'Widget creation error');
                }
                resultWidgetId = widgetData.widgetId;
                updatedAtTimestamp = widgetData.updated_at;
                setWidgetId(resultWidgetId);
            }

            const url = `https://cta.lqr.ai/api/drawer/v2?id=${resultWidgetId}&debug_fresh=1`;
            setScriptTag(`<script src="${url}"></script>`);
            setWidgetUpdatedAt(updatedAtTimestamp);
            setIsGenerated(true);
            setCopied(false);
            setLastGeneratedConfig(configuration);

        } catch (error) {
            console.error('Error creating/updating widget:', error);
            alert(`Error: ${error.message}`);
        } finally {
            setIsGenerating(false);
        }
    };

    const generateScriptTag = useCallback(() => {
        createWidgetRecord();
    }, [configuration]);
    
    const handleGenerateScript = () => {
        generateScriptTag();
    };

    // Initial generation effect - prefer existing widget id if available
    useEffect(() => {
        if (!hasInitialGenerated) {
            const existingId = widgetId || generator?.id || null;
            const url = existingId
                ? `https://cta.lqr.ai/api/drawer/v2?id=${existingId}&debug_fresh=1`
                : `https://cta.lqr.ai/api/drawer`;
            setScriptTag(`<script src="${url}"></script>`);
            setIsGenerated(Boolean(existingId));
            setHasInitialGenerated(true);
            setCopied(false);
        }
    }, [hasInitialGenerated, widgetId, generator?.id]); // Use separate flag to track initial generation

    const hasChanges = useMemo(() => {
        return !deepEqual(configuration, lastGeneratedConfig);
    }, [configuration, lastGeneratedConfig]);

    const cleanScriptTag = useMemo(() => {
        if (!scriptTag) return '';
        const match = scriptTag.match(/src="([^"]+)"/);
        if (!match) return scriptTag;
        
        // Convert Supabase timestamp to version string (milliseconds since epoch)
        const versionParam = widgetUpdatedAt 
            ? new Date(widgetUpdatedAt).getTime().toString()
            : Date.now().toString();
        
        try {
            const urlObj = new URL(match[1]);
            urlObj.searchParams.delete('debug_fresh');
            urlObj.searchParams.set('v', versionParam);
            const cleaned = urlObj.toString();
            return scriptTag.replace(match[1], cleaned);
        } catch {
            // Fallback: strip debug_fresh via regex if URL parsing fails
            const cleanedUrl = match[1]
                .replace(/([?&])debug_fresh=1(&)?/, (full, sep, hasNext) => (hasNext ? sep : ''))
                .replace(/([?&])v=[^&]+(&)?/, (full, sep, hasNext) => (hasNext ? sep : ''));
            const joinChar = cleanedUrl.includes('?') ? '&' : '?';
            const versionedUrl = `${cleanedUrl}${joinChar}v=${versionParam}`;
            return scriptTag.replace(match[1], versionedUrl);
        }
    }, [scriptTag, widgetUpdatedAt]);

    // Determine if script section should be shown and copy enabled
    const shouldShowScript = useMemo(() => {
        // Show for existing widgets (widgetId exists)
        // Show for new widgets after they've been generated (isGenerated true)
        return widgetId || isGenerated;
    }, [widgetId, isGenerated]);

    const isCopyEnabled = useMemo(() => {
        // Enable copy only if we have a script tag and (existing widget OR generated)
        return Boolean(cleanScriptTag && shouldShowScript);
    }, [cleanScriptTag, shouldShowScript]);

    const handleReset = () => {
        setConfiguration(getInitialConfig());
    };

    const handleUpdateStyling = async () => {
        try {
            if (!configuration.qrId) {
                return;
            }
            if (!widgetId && !generator?.id) {
                return;
            }

            const targetWidgetId = widgetId || generator?.id;

            // Prefer styling from qr_options, fallback to liquidQRs
            let qrStyleSource = null;
            if (qr_options && typeof qr_options === 'object') {
                // qr_options may be a map keyed by qr id, or include current selection
                qrStyleSource = qr_options[configuration.qrId]?.qr_style || qr_options.qr_style || null;
            }
            if (!qrStyleSource) {
                const qrDataObj = liquidQRs.find(qr => qr.id === configuration.qrId);
                qrStyleSource = qrDataObj?.data?.qr_style || null;
            }
            if (!qrStyleSource) {
                return;
            }

            const payload = {
                // QR styling configuration (flatten the nested objects)
                qr_width: qrStyleSource.width,
                qr_height: qrStyleSource.height,
                qr_margin: qrStyleSource.margin,
                qr_type: qrStyleSource.type,
                qr_type_number: qrStyleSource.qrOptions?.typeNumber,
                qr_mode: qrStyleSource.qrOptions?.mode,
                qr_error_correction_level: qrStyleSource.qrOptions?.errorCorrectionLevel,
                qr_shape: qrStyleSource.shape,

                // Dots styling
                qr_dots_color: qrStyleSource.dotsOptions?.color,
                qr_dots_type: qrStyleSource.dotsOptions?.type,
                qr_dots_gradient: qrStyleSource.dotsOptions?.gradient,

                // Corner squares
                qr_corners_square_color: qrStyleSource.cornersSquareOptions?.color,
                qr_corners_square_type: qrStyleSource.cornersSquareOptions?.type,
                qr_corners_square_gradient: qrStyleSource.cornersSquareOptions?.gradient,
                
                // Corner dots
                qr_corners_dot_color: qrStyleSource.cornersDotOptions?.color,
                qr_corners_dot_type: qrStyleSource.cornersDotOptions?.type,
                qr_corners_dot_gradient: qrStyleSource.cornersDotOptions?.gradient,
                
                // Background
                qr_background_color: qrStyleSource.backgroundOptions?.color,
                qr_background_gradient: qrStyleSource.backgroundOptions?.gradient,

                // Image/logo
                qr_image: qrStyleSource.image,
                qr_image_hide_background_dots: qrStyleSource.imageOptions?.hideBackgroundDots,
                qr_image_size: qrStyleSource.imageOptions?.imageSize,
                qr_image_margin: qrStyleSource.imageOptions?.margin,
                qr_image_cross_origin: qrStyleSource.imageOptions?.crossOrigin,

                // Tab animation
                qr_animate: configuration.animateTab,
                qr_animate_type: configuration.animateType
            };

            const result = await updateWidget(targetWidgetId, payload);
            if (!result.ok) {
                console.error('Supabase update error:', result.error);
                return;
            }

            // Update the local updated_at state
            if (result.data?.updated_at) {
                setWidgetUpdatedAt(result.data.updated_at);
            }

            // Regenerate script with new version parameter (cache bust)
            const url = `https://cta.lqr.ai/api/drawer/v2?id=${targetWidgetId}&debug_fresh=1`;
            setScriptTag(`<script src="${url}"></script>`);
            setIsGenerated(true);

            // Show success feedback
            setStyleSaved(true);
            // Trigger a preview refresh since styling was explicitly updated
            setPreviewTrigger(prev => prev + 1);
        } catch (err) {
            console.error('Error updating widget styling:', err);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(cleanScriptTag);
            setCopied(true);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const handleConfiguration = (key, value) => {
        setConfiguration(prev => ({...prev, [key]: value}))
    };

    // Debounce preview updates only when visual fields change, or when previewTrigger bumps
    useEffect(() => {
        const update = debounce(() => {
            setDebouncedPreviewConfig(configuration);
        }, 400);
        update();
        return () => {
            update.cancel();
        };
        // Only respond to these specific changes
    }, [configuration.animateTab, configuration.animateType, configuration.qrId, configuration.title, configuration.tabText, configuration.tabColor, configuration.showLogo, previewTrigger, selectedQrStyle]);

    useEffect(() => {
        if (copied) {
            const timer = setTimeout(() => {
                setCopied(false);
            },3000);
            
            return () => clearTimeout(timer);
        }
    },[copied]);

    useEffect(() => {
        if (stylingSaved) {
            const timer = setTimeout(() => {
                setStyleSaved(false);
            }, 3000);
            
            return () => clearTimeout(timer);
        }
    }, [stylingSaved]);

    return (
        <AnimatePresence mode='wait'>
            <motion.div 
                className={styles.container}
                transition={transition}
                variants={variants}
                initial='initial'
                animate='animate'
                exit='exit'
            >
                <div className={styles.content}>
                    <div className={styles.left}>
                        <div className={styles.configuration}>
                            <div className={styles.headerRow}>
                                <h5 className={styles.header}>Widget Configuration</h5>
                                {widgetId && (
                                    <button className={`${styles.updateBtn} ${stylingSaved ? styles.saved : ''}`} onClick={handleUpdateStyling} disabled={isGenerating || stylingSaved}>{stylingSaved ? <><i className='far fa-check' /> Saved</> : 'Update Widget Styling'}</button>
                                )}
                            </div>
                            <div className={styles.body}>
                                <div className={styles.section}>
                                    <div className={styles.inputGroup}>
                                        <label htmlFor='widget-name'>Widget Name</label>
                                        <input type='text' id='widget-name' value={configuration.name} maxLength='50' onChange={e => setConfiguration({...configuration, name: e.target.value})} placeholder='Enter a custom name (optional)' />
                                        <span>{`${configuration.name.length}/50 Characters`}</span>
                                    </div>
                                    <QrSelect liquidQRs={liquidQRs} configuration={configuration} handleConfiguration={handleConfiguration} />
                                    <div className={styles.inputGroup}>
                                        <label htmlFor='display-on'>Show Drawer On...</label>
                                        <select id='display-on' value={configuration.displayOn} onChange={e => setConfiguration({ ...configuration, displayOn: e.target.value })}>
                                            <option value='all'>All Devices</option>
                                            <option value='mobile-only'>Mobile Only ({'<'} 768px)</option>
                                            <option value='tablet-up'>Tablet & Desktop (≥ 768px)</option>
                                            <option value='tablet'>Tablet Only (768px - 1023px)</option>
                                            <option value='desktop'>Desktop Only (≥ 1024px)</option>
                                        </select>
                                        <span>Control which device sizes should display the drawer</span>
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label htmlFor='logo'>LiquidQR Branding</label>
                                        <LogoToggle showLogo={configuration.showLogo} onChange={e => setConfiguration({ ...configuration, showLogo: e.target.checked })} />
                                        <span>Toggle LiquidQR logo display in the tab</span>
                                    </div>
                                </div>
                                <div className={styles.section}>
                                    <div className={styles.headerRow} style={{ marginTop: "0.5rem" }} >
                                      <h5 className={styles.header}>Tab Configuration</h5>
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label htmlFor='tab-text'>Tab Text</label>
                                        <input type='text' id='tab-text' value={configuration.tabText} maxLength='20' onChange={e => setConfiguration({...configuration, tabText: e.target.value})} placeholder='e.g. Connect Now' />
                                        <span>{`${configuration.tabText.length}/20 Characters`}</span>
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label htmlFor='tab-color'>Tab Color</label>
                                        <div className={styles.tabColorWrap}>
                                            {showColorPicker && (
                                                <div className={styles.popover}>
                                                    <div className={styles.cover} onClick={() => setShowColorPicker(false)} />
                                                    <ColorPicker 
                                                        color={configuration.tabColor} 
                                                        onChange={(color) => setConfiguration({ ...configuration, tabColor: color })} 
                                                    />
                                                </div>
                                            )}
                                            <div className={styles.swatch} onClick={() => setShowColorPicker(!showColorPicker)}>
                                                <div className={styles.color} style={{background: configuration.tabColor}} />
                                            </div>
                                            <input type='text' id='tab-color' value={configuration.tabColor} onChange={e => setConfiguration({ ...configuration, tabColor: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label htmlFor='animate'>Tab Animation</label>
                                        <AnimationToggle animateTab={configuration.animateTab} onChange={e => setConfiguration({ ...configuration, animateTab: e.target.checked })} />
                                        <span>Enable animation effects on the tab</span>
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label htmlFor='animate-type'>Animation Style</label>
                                        <AnimationTypeSlider 
                                            animateType={configuration.animateType} 
                                            disabled={!configuration.animateTab}
                                            onChange={(value) => setConfiguration({ ...configuration, animateType: value })} 
                                        />
                                        <span>Choose the animation style for the tab</span>
                                    </div>
                                </div>
                                <div className={styles.section}>
                                  <div className={styles.headerRow} style={{ marginTop: "0.5rem" }}>
                                    <h5 className={styles.header}>Drawer Configuration</h5>
                                  </div>
                                  <div className={styles.inputGroup}>
                                    <label htmlFor='title-text'>Drawer Text</label>
                                    <input type='text' id='title-text' value={configuration.title} maxLength='50' onChange={e => setConfiguration({...configuration, title: e.target.value})} placeholder='Enter a Title' />
                                    <span>{`${configuration.title.length}/50 Characters`}</span>
                                  </div>
                                </div>
                                <div className={styles.keyInfo}>
                                    <i className='far fa-lightbulb-on' /> <strong>Character Limits</strong>: Text limits ensure optimal display on all devices. Tab text is kept short for the vertical layout, while titles are limited for mobile compatibility.
                                </div>
                                <div className={styles.section} style={{marginTop: '1rem'}}>
                                    <div className={styles.inputGroup} style={{flexDirection: 'row', flexWrap: 'nowrap', gap: '0 .5rem'}}>
                                        <button className={styles.generateBtn} onClick={handleGenerateScript} disabled={!hasChanges}>
                                            {widgetId ? 'Save Widget' : 'Generate Script'}
                                        </button>
                                        <button className={styles.resetBtn} onClick={handleReset} disabled={isGenerating}>Reset</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.script}>
                            <h5 className={styles.header}>Generated Script</h5>
                            <AnimatePresence>
                                {hasChanges && (
                                    <motion.div 
                                        className={styles.changes}
                                        transition={transition}
                                        variants={variants}
                                        initial='hide'
                                        animate='show'
                                        exit='hide'
                                    >Changes Pending</motion.div>
                                )}
                            </AnimatePresence>
                            {shouldShowScript ? (
                                <div className={styles.inputGroup} style={{marginTop: '.5rem'}}>
                                    <label htmlFor='script'>Copy this script tag to your website</label>
                                    <div className={styles.textAreaWrap}>
                                        <textarea id='script' value={cleanScriptTag} disabled={true} />
                                        <button className={`${styles.copyBtn} ${copied ? styles.copied : ''}`} onClick={handleCopy} disabled={!isCopyEnabled}>{copied ? <i className='far fa-check' /> : 'Copy'}</button>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.inputGroup} style={{marginTop: '.5rem'}}>
                                    <label htmlFor='script'>Generate a widget first to see the script tag</label>
                                    <div className={styles.textAreaWrap}>
                                        <textarea id='script' value='' disabled={true} placeholder='Click "Generate Script" to create a widget and see the code here' />
                                    </div>
                                </div>
                            )}
                            <div className={styles.keyInfo} style={{padding: '.75rem', marginTop: '1rem'}}>
                                <div><i className='far fa-clipboard-list' /> Integration Instructions</div>
                                <ul>
                                    <li>Paste the script tag before the closing {`${'</body>'}`} tag</li>
                                    <li>The drawer will automatically load when the page loads</li>
                                    <li>Test on a staging environment first</li>
                                    <li>Make sure your image URL is publicly accessible</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className={styles.right}>
                        <div className={styles.preview}>
                            <h5 className={styles.header}>Live Preview</h5>
                            <div className={styles.body}>
                                <div className={styles.sectionPreview}>
                                    <FramePreview 
                                        scriptTag={scriptTag} 
                                        previewConfig={debouncedPreviewConfig} 
                                        selectedQrStyle={selectedQrStyle}
                                        connectDefaultUrl={connectDefaultUrl}
                                        useDummyPreview={hasChanges} 
                                        refreshKey={widgetUpdatedAt || previewTrigger}
                                    />
                                </div>
                                <div className={styles.section}>
                                    <div className={styles.previewWords}>
                                        <i className='far fa-square-check' /> This is your actual script running in a real environment
                                    </div>
                                    {!isGenerated && (
                                        <div className={styles.previewWords}>
                                            <i className='far fa-triangle-exclamation' /> Preview shows previous version - click &quot;Generate Script&quot; to update
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

const QrSelect = ({liquidQRs, configuration, handleConfiguration}) => (
    <div className={styles.inputGroup}>
        <label htmlFor='qr-id'>QR Code ID</label>
        <select id='qr-id' value={configuration.qrId || '0'} onChange={e => handleConfiguration('qrId', e.target.value)}>
            <option disabled value='0'>- Select a QR -</option>
            {liquidQRs.map((qr) => (
                <option key={qr.id} value={qr.id}>{qr.data.nickname} - {qr.id}</option>
            ))}
        </select>
    </div>
);

const LogoToggle = ({ showLogo, onChange }) => (
    <div className={styles.typeWrap}>
        <motion.label
            className={styles.type}
            transition={transition}
            variants={variants}
            initial={false}
            animate={showLogo ? 'on' : 'off'}
        >
            <input type='checkbox' id='logo' checked={showLogo} onChange={onChange} />
            <motion.div
                className={styles.toggle}
                transition={transition}
                variants={variants}
                initial={false}
                animate={showLogo ? 'tShow' : 'tHide'}
            >
                <motion.i
                    className='far fa-check'
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={showLogo ? 'show' : 'hide'}
                />
            </motion.div>
        </motion.label>
        <motion.span 
            className={styles.typeLabel}
            transition={transition}
            variants={variants}
            initial={false}
            animate={showLogo ? 'lShow' : 'lHide'}
        >Show</motion.span>
    </div>
);

const AnimationToggle = ({ animateTab, onChange }) => (
    <div className={styles.typeWrap}>
        <motion.label
            className={styles.type}
            transition={transition}
            variants={variants}
            initial={false}
            animate={animateTab ? 'on' : 'off'}
        >
            <input type='checkbox' id='animate' checked={animateTab} onChange={onChange} />
            <motion.div
                className={styles.toggle}
                transition={transition}
                variants={variants}
                initial={false}
                animate={animateTab ? 'tShow' : 'tHide'}
            >
                <motion.i
                    className='far fa-check'
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={animateTab ? 'show' : 'hide'}
                />
            </motion.div>
        </motion.label>
        <motion.span 
            className={styles.typeLabel}
            transition={transition}
            variants={variants}
            initial={false}
            animate={animateTab ? 'lShow' : 'lHide'}
        >Enabled</motion.span>
    </div>
);

const AnimationTypeSlider = ({ animateType, disabled, onChange }) => (
    <div className={styles.typeWrap} style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
        <motion.label
            className={styles.type}
            style={{ backgroundColor: '#edf0f6', border: '2px solid #607ca7' }}
            transition={transition}
            variants={variants}
            initial={false}
            animate='on'
        >
            <input 
                type='checkbox' 
                checked={animateType === 'peak'} 
                onChange={(e) => onChange(e.target.checked ? 'peak' : 'wiggle')}
                disabled={disabled}
            />
            <motion.div
                className={styles.toggle}
                transition={transition}
                initial={false}
                animate={{
                    left: animateType === 'peak' ? '1.75rem' : '.25rem',
                    backgroundColor: '#607ca7'
                }}
            />
        </motion.label>
        <motion.span 
            className={styles.typeLabel}
            style={{ minWidth: '4rem', display: 'inline-block' }}
            transition={transition}
            variants={variants}
            initial={false}
            animate='lShow'
        >{animateType === 'peak' ? 'Peak' : 'Wiggle'}</motion.span>
    </div>
);